import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getOnboardingStatus,
  saveOnboardingStep,
  completeOnboarding,
  skipOnboarding,
  generatePromptPreview
} from '../api/onboardingApi';
import {
  Building2,
  Clock,
  Bot,
  Mail,
  MessageCircle,
  Phone,
  Palette,
  Users,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  SkipForward,
  Loader2,
  Briefcase,
  FileWarning,
  Upload,
  X
} from '../icons/wp-icons';

// Schritt-Komponenten
import BrancheStep from '../components/Onboarding/BrancheStep';
import FirmendatenStep from '../components/Onboarding/FirmendatenStep';
import DatenImportStep from '../components/Onboarding/DatenImportStep';
import OeffnungszeitenStep from '../components/Onboarding/OeffnungszeitenStep';
import KiAgentStep from '../components/Onboarding/KiAgentStep';
import EmailStep from '../components/Onboarding/EmailStep';
import TelefonAgentStep from '../components/Onboarding/TelefonAgentStep';
import MahnwesenStep from '../components/Onboarding/MahnwesenStep';
import BrandingStep from '../components/Onboarding/BrandingStep';
import TeamStep from '../components/Onboarding/TeamStep';
import WhatsAppAgentStep from '../components/Onboarding/WhatsAppAgentStep';
import AbschlussStep from '../components/Onboarding/AbschlussStep';

const STEPS = [
  { id: 1, title: 'Branche', icon: Briefcase, description: 'Was ist Ihr Gewerk?' },
  { id: 2, title: 'Firmendaten', icon: Building2, description: 'Ihre Unternehmensdaten' },
  { id: 3, title: 'Datenimport', icon: Upload, description: 'Daten aus bisheriger Software übernehmen' },
  { id: 4, title: 'Öffnungszeiten', icon: Clock, description: 'Wann sind Sie erreichbar?' },
  { id: 5, title: 'KI-Assistent', icon: Bot, description: 'Wie soll Ihr Assistent arbeiten?' },
  { id: 6, title: 'E-Mail', icon: Mail, description: 'E-Mail-Konto verbinden' },
  { id: 7, title: 'WhatsApp', icon: MessageCircle, description: 'WhatsApp-Assistent einrichten' },
  { id: 8, title: 'Telefon', icon: Phone, description: 'KI-Telefonassistent einrichten' },
  { id: 9, title: 'Mahnwesen', icon: FileWarning, description: 'Automatische Zahlungserinnerungen' },
  { id: 10, title: 'Design', icon: Palette, description: 'Farben und Logo anpassen' },
  { id: 11, title: 'Team', icon: Users, description: 'Mitarbeiter einladen' },
  { id: 12, title: 'Fertig', icon: CheckCircle2, description: 'Alles bereit!' }
];

// Agent-Modus Schritte (nur KI-relevante Schritte)
const AGENT_STEPS = [1, 4, 5]; // Branche, Öffnungszeiten, KI-Agent

function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();

  // Prüfe ob Agent-Modus aktiv ist
  const isAgentMode = searchParams.get('mode') === 'agent';

  const [currentStep, setCurrentStep] = useState(isAgentMode ? 1 : 1);
  const [agentStepIndex, setAgentStepIndex] = useState(0); // Index in AGENT_STEPS
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [onboardingData, setOnboardingData] = useState({
    branchenKonfiguration: null,
    firmendaten: null
  });

  // Verwende useRef für stepData um Re-Renders zu vermeiden
  const stepDataRef = useRef({});
  const [stepValid, setStepValid] = useState(false);

  // Im Agent-Modus: Sammle alle Daten und speichere erst am Ende
  const [agentModeData, setAgentModeData] = useState({
    step1: null, // Branche
    step4: null, // Öffnungszeiten
    step5: null  // KI-Agent
  });

  // Onboarding-Status beim Laden prüfen
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const status = await getOnboardingStatus();

        // Im Agent-Modus: Nicht zum Dashboard redirecten, aber Daten laden
        if (!isAgentMode && status.onboardingCompleted) {
          navigate('/dashboard', { replace: true });
          return;
        }

        // Im Agent-Modus: Starte beim ersten Agent-Schritt
        if (isAgentMode) {
          setCurrentStep(AGENT_STEPS[0]);
          setAgentStepIndex(0);
        } else {
          // Normaler Modus: Aktuellen Schritt setzen
          setCurrentStep(Math.max(status.currentStep + 1, 1));
        }

        setOnboardingData({
          branchenKonfiguration: status.branchenKonfiguration,
          firmendaten: status.firmendaten
        });
      } catch (err) {
        console.error('Fehler beim Laden des Status:', err);
        setError('Fehler beim Laden des Onboarding-Status');
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [navigate, isAgentMode]);

  // Theme-Color für Onboarding-Seite setzen
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const originalColor = metaThemeColor?.getAttribute('content');

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', '#378ab2');
    }

    return () => {
      if (metaThemeColor && originalColor) {
        metaThemeColor.setAttribute('content', originalColor);
      }
    };
  }, []);

  const handleNext = async () => {
    setError('');
    setSaving(true);

    try {
      const dataToSave = stepDataRef.current;

      // Agent-Modus Logik - Daten sammeln und erst am Ende speichern
      if (isAgentMode) {
        // Daten für aktuellen Schritt zwischenspeichern (nicht an Backend senden)
        setAgentModeData(prev => ({
          ...prev,
          [`step${currentStep}`]: dataToSave
        }));

        const nextAgentIndex = agentStepIndex + 1;
        if (nextAgentIndex < AGENT_STEPS.length) {
          // Nächster Agent-Schritt - noch nicht speichern
          setAgentStepIndex(nextAgentIndex);
          setCurrentStep(AGENT_STEPS[nextAgentIndex]);
          stepDataRef.current = {};
          setStepValid(false);
        } else {
          // Agent-Modus abgeschlossen - JETZT alle Daten speichern
          // Sammle alle Daten inklusive des aktuellen Schritts
          const allAgentData = {
            ...agentModeData,
            [`step${currentStep}`]: dataToSave
          };

          // Alle Schritte nacheinander speichern
          for (const stepId of AGENT_STEPS) {
            const stepData = allAgentData[`step${stepId}`];
            if (stepData) {
              await saveOnboardingStep(stepId, stepData);
            }
          }

          // System-Prompt generieren
          await generatePromptPreview();
          // Zurück zu den Einstellungen
          navigate('/einstellungen', { replace: true });
        }
      } else {
        // Normaler Modus - bei jedem Schritt speichern
        // Step 12 (Abschluss) hat keine zu speichernden Daten
        if (currentStep <= 11) {
          await saveOnboardingStep(currentStep, dataToSave);
        }

        // Bei Schritt 1-2 die lokalen Daten aktualisieren
        if (currentStep === 1) {
          setOnboardingData(prev => ({
            ...prev,
            branchenKonfiguration: { ...prev.branchenKonfiguration, ...dataToSave }
          }));
        } else if (currentStep === 2) {
          setOnboardingData(prev => ({
            ...prev,
            firmendaten: { ...prev.firmendaten, ...dataToSave }
          }));
        }

        if (currentStep < 12) {
          // Steps 1-11: Nächsten Schritt anzeigen
          setCurrentStep(currentStep + 1);
          stepDataRef.current = {};
          setStepValid(false);
        } else {
          // Step 12 (Abschluss): Onboarding abschließen
          await completeOnboarding();
          await refreshUser?.();
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (isAgentMode) {
      if (agentStepIndex > 0) {
        const prevAgentIndex = agentStepIndex - 1;
        setAgentStepIndex(prevAgentIndex);
        setCurrentStep(AGENT_STEPS[prevAgentIndex]);
        stepDataRef.current = {};
        setStepValid(false);
      }
    } else {
      if (currentStep > 1) {
        setCurrentStep(currentStep - 1);
        stepDataRef.current = {};
        setStepValid(false);
      }
    }
  };

  const handleSkip = async () => {
    if (isAgentMode) {
      // Im Agent-Modus: Einfach zurück zu Einstellungen
      navigate('/einstellungen', { replace: true });
      return;
    }

    if (window.confirm('Möchten Sie das Onboarding wirklich überspringen? Sie können es später in den Einstellungen nachholen.')) {
      try {
        await skipOnboarding();
        await refreshUser?.();
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Fehler beim Überspringen:', err);
        setError('Fehler beim Überspringen');
      }
    }
  };

  const handleClose = () => {
    navigate('/einstellungen', { replace: true });
  };

  // Stabile Callback-Funktion die keine Re-Renders verursacht
  const handleStepDataChange = useCallback((data, isValid = true) => {
    stepDataRef.current = data;
    setStepValid(isValid);
  }, []);

  // Memoize die Props für die Schritt-Komponenten
  const stepProps = useMemo(() => ({
    onDataChange: handleStepDataChange,
    onboardingData,
    user
  }), [handleStepDataChange, onboardingData, user]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BrancheStep {...stepProps} />;
      case 2:
        return <FirmendatenStep {...stepProps} />;
      case 3:
        return <DatenImportStep {...stepProps} />;
      case 4:
        return <OeffnungszeitenStep {...stepProps} />;
      case 5:
        return <KiAgentStep {...stepProps} />;
      case 6:
        return <EmailStep {...stepProps} />;
      case 7:
        return <WhatsAppAgentStep {...stepProps} />;
      case 8:
        return <TelefonAgentStep {...stepProps} />;
      case 9:
        return <MahnwesenStep {...stepProps} />;
      case 10:
        return <BrandingStep {...stepProps} />;
      case 11:
        return <TeamStep {...stepProps} />;
      case 12:
        return <AbschlussStep {...stepProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#378ab2] mx-auto mb-4" />
          <p className="text-gray-600">Lade Onboarding...</p>
        </div>
      </div>
    );
  }

  const currentStepInfo = STEPS[currentStep - 1];
  const agentModeSteps = AGENT_STEPS.map(id => STEPS[id - 1]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/icons/icon-192x192.png"
                alt="WerkPilot"
                className="w-10 h-10"
              />
              <div>
                <span className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  WerkPilot
                </span>
                {isAgentMode && (
                  <span className="ml-2 text-sm text-purple-600 font-medium">
                    KI-Agent Assistent
                  </span>
                )}
              </div>
            </div>
            {isAgentMode ? (
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Schließen
              </button>
            ) : (
              <button
                onClick={handleSkip}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
              >
                <SkipForward className="w-4 h-4" />
                Überspringen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center gap-0 mb-4">
            {(isAgentMode ? agentModeSteps : STEPS).map((step, index) => {
              const Icon = step.icon;
              const isActive = isAgentMode
                ? index === agentStepIndex
                : currentStep === step.id;
              const isCompleted = isAgentMode
                ? index < agentStepIndex
                : currentStep > step.id;
              const totalSteps = isAgentMode ? agentModeSteps.length : STEPS.length;

              return (
                <div key={step.id} className="flex items-center">
                  <div className={`
                    flex items-center justify-center w-10 h-10 rounded-full transition-all
                    ${isActive ? 'bg-[#378ab2] text-white ring-4 ring-[#378ab2]/20' : ''}
                    ${isCompleted ? 'bg-green-500 text-white' : ''}
                    ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-500' : ''}
                  `}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  {index < totalSteps - 1 && (
                    <div className={`
                      hidden sm:block w-12 lg:w-20 h-1 mx-2
                      ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}
                    `} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">
              Schritt {isAgentMode ? agentStepIndex + 1 : currentStep} von {isAgentMode ? AGENT_STEPS.length : STEPS.length}
            </p>
            <h2 className="text-xl font-semibold text-gray-900 mt-1">
              {currentStepInfo.title}
            </h2>
            <p className="text-gray-600 text-sm">
              {currentStepInfo.description}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 pb-32 md:pb-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-6 md:p-8">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <button
            onClick={handleBack}
            disabled={(isAgentMode ? agentStepIndex === 0 : currentStep === 1) || saving}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-button font-medium transition
              ${(isAgentMode ? agentStepIndex === 0 : currentStep === 1)
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'}
            `}
          >
            <ArrowLeft className="w-5 h-5" />
            Zurück
          </button>

          <button
            onClick={handleNext}
            disabled={saving || (!stepValid && (isAgentMode || (currentStep < 6 && currentStep !== 3)))}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-button font-medium transition
              ${saving || (!stepValid && (isAgentMode || (currentStep < 6 && currentStep !== 3)))
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#378ab2] text-white hover:bg-[#2a6d8a]'}
            `}
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Speichern...
              </>
            ) : (isAgentMode && agentStepIndex === AGENT_STEPS.length - 1) ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Fertig
              </>
            ) : (!isAgentMode && currentStep === STEPS.length) ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Fertig
              </>
            ) : (
              <>
                Weiter
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
